import { Close } from '@mui/icons-material'
import { Divider, Drawer } from '@mui/material'
import { observer } from 'mobx-react-lite'
import React, { Fragment, useRef } from 'react'
import { I18n, MY_PLACE_SELLER, useClickOutsideDetector } from 'common'
import { useUiStateStore } from '../../../../../src/store/uiState/UiStateStore'
import { useSearchStore } from '../../../../../src/store/search/SearchStore'
import { defaultTheme } from '../../../../../src/theme/index'
import { zIndex } from '../../../../../src/theme/zIndex'
import { Button } from '../../../../../src/ui-components/forms/Button'
import styled from 'styled-components'
import miscellaneousVehicle from '../../../../../src/features/search/Results/utils/miscellaneousVehicle'
import { MultiPartInquiryModel } from '../../../../../src/store/models/SearchModels'
import { setQtyAtLocationBulk } from 'cart'
import { useResultsStore } from 'results'
import { hawkSearchProvider } from '../../../../../src/config/analytics/core/providers/HawkSearchServiceProvider'
import { prepareEventData } from '../../../../../src/helpers/HawkSearchEventHelpers'
import { Vehicle } from '../../../../../src/store/models/Vehicles'
import { RootState } from 'store'
import { useSelector } from 'react-redux'
import { formatVehicleDisplayWithOutEngine } from 'src/helpers/utils'

export const MultiPartInquiryCartSelector = observer((): React.ReactElement => {
  const { closeCartSelectorModal, cartSelectorModal, setShowAddAlltoCart } =
    useSearchStore()

  const { multiPartInquiryResults } = useResultsStore()
  const onClose = closeCartSelectorModal
  const show = cartSelectorModal
  const ref = useRef<HTMLInputElement>(null)

  useClickOutsideDetector([ref], show, onClose)

  const uiStore = useUiStateStore()
  const cartVehicles = useSelector(
    (state: RootState) => state.cart.cartData.vehicles
  )

  function defaultModal() {
    return (
      <Container ref={ref}>
        <FlexColumn style={{ paddingBottom: 20 }}>
          <FlexRow style={{ justifyContent: 'space-between' }}>
            <Title>
              <I18n name="selectCartToAddItem" />
            </Title>
            <StyledClose onClick={onClose} />
          </FlexRow>
        </FlexColumn>

        <Divider style={{ marginLeft: -24, marginRight: -24, height: 0.2 }} />
        <FlexColumn style={{ paddingBottom: 20, paddingTop: 34 }}>
          {cartVehicles?.map((item) => (
            <Fragment key={JSON.stringify(item.vehicle)}>
              {
                // TODO: check if we can use `isMiscellaneousVehicle` here.
                item?.vehicle?.make?.value !== 'MISCELLANEOUS' && (
                  <>
                    <ListAddToCar>
                      <ListAddToCarText>
                        {formatVehicleDisplayWithOutEngine(
                          item?.vehicle,
                          false
                        )}
                      </ListAddToCarText>
                      <ButtonWrapper>
                        <AddButton
                          onClick={() => {
                            handleAddPartsToCart(
                              item?.vehicle,
                              multiPartInquiryResults
                            )

                            uiStore.displaySuccessNotification(
                              'partsHaveBeenAddedToYourCart'
                            )
                            setShowAddAlltoCart(false)
                            onClose()
                          }}
                          text="addToCart"
                        />
                      </ButtonWrapper>
                    </ListAddToCar>
                    <Divider
                      style={{
                        marginLeft: 0,
                        marginRight: 0,
                        marginBottom: 20,
                        marginTop: 20,
                      }}
                    />
                  </>
                )
              }
            </Fragment>
          ))}
        </FlexColumn>

        <FlexColumn>
          <ListAddToCar>
            <ListAddToCarText>
              <I18n name="miscellaneous" />
            </ListAddToCarText>
            <ButtonWrapper>
              <AddButton
                onClick={() => {
                  handleAddPartsToCart(
                    miscellaneousVehicle,
                    multiPartInquiryResults
                  )
                  uiStore.displaySuccessNotification(
                    'partsHaveBeenAddedToYourCart'
                  )
                  setShowAddAlltoCart(false)
                  onClose()
                }}
                text="addToCart"
              />
            </ButtonWrapper>
          </ListAddToCar>
        </FlexColumn>
        <Divider style={{ marginLeft: 0, marginRight: 0, marginTop: 20 }} />
      </Container>
    )
  }

  const width = window.innerWidth

  const handleAddPartsToCart = async (
    vehicle: Vehicle,
    multiPartInquiryModels: MultiPartInquiryModel[]
  ) => {
    await setQtyAtLocationBulk(
      multiPartInquiryModels.map((model) => ({
        product: model.part,
        locationId: model?.activeLocation?.locationId,
        quantity: model.qty,
        vehicle,
        seller: MY_PLACE_SELLER,
      }))
    )
    const seen = new Set<number>()
    multiPartInquiryModels.forEach((model) => {
      if (seen.has(model.part.lineNo)) return
      seen.add(model.part.lineNo)
      hawkSearchProvider.trackEvent(
        'Add2Cart',
        prepareEventData('Add2Cart', {
          productId:
            model.part.allianceProductId ||
            `${model.part.lineCode} ${model.part.partNumber}`,
          price: model.activeLocation?.cost,
          quantity: model.qty,
          vehicle,
        })
      )
    })
  }

  return (
    <ResponsiveDrawer
      anchor={width >= 1024 ? 'right' : 'bottom'}
      open={show}
      onClose={onClose}
      PaperProps={{
        square: false,
      }}
    >
      {defaultModal()}
    </ResponsiveDrawer>
  )
})

const ResponsiveDrawer = styled(Drawer)`
  z-index: ${zIndex.modal};
`

const StyledClose = styled(Close)`
  font-size: 30px;
  cursor: pointer;
`

const Container = styled.div`
  height: 100%;
  width: 100%;
  background-color: ${defaultTheme.colors.componentsBackground};
  padding: 29px 24px 74px 24px;
  overflow-y: auto;
`

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`
const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
`
const Title = styled.span`
  font-family: 'Oswald';
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 30px;
  padding-bottom: 20px;
`

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
`
const AddButton = styled(Button)`
  width: 100%;
`

const ListAddToCar = styled.div`
  display: flex;
  align-items: center;
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 24px;
  gap: 10px;
`

const ListAddToCarText = styled.div`
  font-weight: bold;
  width: 100%;
  p {
    margin: 0px;
  }
`
